import React, {useState, useEffect, useRef} from "react";
import {Badge, Col, Row} from "antd";
import {t} from "i18next";
import moment from "moment";
import {DownloadOutlined} from "@ant-design/icons";
import {useHistory} from "react-router-dom";
import stores from "../../store/stores";
import {useFetch} from "../../hooks/useFetch";
import useIsFocused from "../../hooks/useIsFocused";
import ShiftManagementFilters from "../shiftManagement/Components/ShiftManagementFilters";
import ShiftManagementTable from "../shiftManagement/Components/ShiftManagementTable";
import {normalizeShiftReportRows} from "../shiftManagement/Components/normalizeShiftRow";
import {toShiftReportStatusParam} from "../../constants/shiftStatuses";
import {ShiftScoreCourierStatusFilter} from "../_defaultComponents/FilterComponents";
import {CustomButton} from "../_defaultComponents/CustomButton/CustomButton";
import {ButtonsRow, WaitingApproveButton} from "./ShiftScoreTableStyled";

// Liste tek istekte çekilip sayfalama istemcide yapılıyor; sayfa geçişi istek üretmiyor.
const FETCH_LIMIT = 200;
const REFRESH_INTERVAL = 30000;

/**
 * Firma kullanıcısının vardiya ekranı. Vardiya Yönetimi ile aynı filtre barını, aynı satır
 * normalizasyonunu ve aynı tabloyu kullanır; yalnız firma kullanıcısına özel butonlar burada.
 */
const ShiftScore = () => {
    const history = useHistory();
    const [currentPage, setCurrentPage] = useState(1);
    const [reloadCount, setReloadCount] = useState(false);
    const [reloadData, setReloadData] = useState({});
    const [recall, setRecall] = useState(true);
    const [currentStatus, setCurrentStatus] = useState("all");
    const isFocused = useIsFocused();

    const recallRef = useRef(recall);
    recallRef.current = recall;

    useEffect(() => {
        if (isFocused) {
            const timeout = setTimeout(async () => {
                await setRecall(!recallRef.current);
            }, REFRESH_INTERVAL);

            return () => clearTimeout(timeout);
        }
    }, [recall, isFocused]);

    const [filters, setFilters] = useState({
        city: "all",
        company: undefined,
        subcompany: [],
        pool: [],
        status: "all",
        period: "today",
        startDate: moment()
            .subtract(1, "d")
            .toDate(),
        endDate: moment().toDate(),
        date: moment().toDate(),
        searchText: undefined
    });

    const [loading, data] = useFetch({
        fetchFn: stores.shiftStore.getShiftScoreList.bind(null, {
            limit: FETCH_LIMIT,
            skip: 0,
            companies: filters.company ? [filters.company] : ["all"],
            subcompanies: filters.subcompany?.length ? filters.subcompany : undefined,
            period: filters.period,
            startDate: filters.startDate,
            endDate: filters.endDate,
            status: toShiftReportStatusParam(filters.status),
            city: filters.city,
            searchText: filters.searchText,
            currentStatus
        }),
        dependencies: [filters, currentStatus, reloadData, recall]
    });

    const [, count] = useFetch({
        defaultDataValue: 0,
        initialCall: true,
        fetchFn: stores.shiftStore.getWaitingApproveCount.bind(null, {}),
        dependencies: [reloadData, recall, reloadCount],
        condition: stores.meStore.checkRole("isCompanyUser")
    });

    const normalizedRows = normalizeShiftReportRows(data?.rows);
    // Vardiya akışı durumları (Vardiyada - ..., Tamamlandı) puantaj ucunda filtrelenemediği
    // için daraltma istemcide yapılıyor; bkz. ShiftManagement.js
    const rows =
        filters.status === "all"
            ? normalizedRows
            : normalizedRows.filter(row => row.status === filters.status);

    const onClickWaitingApprove = () => {
        setFilters({...filters, status: "Onay Bekliyor", period: "", startDate: "", endDate: ""});
        setCurrentPage(1);
    };

    const onClickExport = async () => {
        const response = await stores.shiftStore.exportShiftScoreListByCurrentStatus({
            ...filters,
            currentStatus
        });
        const link = document.createElement("a");
        link.href = response;
        link.download = `vardiya-durum-raporu-${moment().format("YYYY-MM-DD-HH-mm-ss")}.xlsx`;
        document.body.appendChild(link);
        link.click();
        setTimeout(() => window.URL.revokeObjectURL(link), 200);
    };

    return (
        <div>
            <Row gutter={[8, 8]}>
                <ShiftManagementFilters
                    setCurrentPage={setCurrentPage}
                    filters={filters}
                    setFilters={setFilters}
                    workType="dedicated"
                    setWorkType={() => {}}
                />
                <Col xs={24} sm={12} md={8} lg={8} xl={3}>
                    <h5>Kurye Durumu</h5>
                    <ShiftScoreCourierStatusFilter
                        value={currentStatus}
                        onChange={value => {
                            setCurrentStatus(value);
                            setCurrentPage(1);
                        }}
                        showAllOption
                        hideTitle
                    />
                </Col>
            </Row>
            <Row align="middle" justify="end">
                <Col span={24}>
                    <ButtonsRow>
                        {currentStatus && currentStatus !== "all" && (
                            <CustomButton
                                type="primary"
                                data-testid="exportWithExcelButton"
                                onClick={onClickExport}
                                icon={<DownloadOutlined />}
                            >
                                {t("companyDetail.actions.exportWithExcel", {ns: "companyDetail"})}
                            </CustomButton>
                        )}
                        {stores.meStore.checkRole("isCompanyUser") && count > 0 ? (
                            <Badge count={count} style={{zIndex: 3}}>
                                <WaitingApproveButton
                                    style={{color: "red"}}
                                    onClick={onClickWaitingApprove}
                                >
                                    {t("shiftScore.shiftStatuses.waitingApprove", {
                                        ns: "shiftScore"
                                    })}
                                </WaitingApproveButton>
                            </Badge>
                        ) : (
                            ""
                        )}
                        <CustomButton
                            data-testid="objectionsButton"
                            onClick={() => history.push("/reports/shift-score/objections")}
                        >
                            {t("screens.shiftReportObjections", {ns: "common"})}
                        </CustomButton>
                    </ButtonsRow>
                </Col>
            </Row>
            <Row style={{marginTop: 16}}>
                <ShiftManagementTable
                    loading={loading}
                    data={rows}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    setReloadData={() => {
                        setReloadData(!reloadData);
                        setReloadCount(!reloadCount);
                    }}
                    workType="dedicated"
                    useReportActions
                />
            </Row>
        </div>
    );
};

export default ShiftScore;
